import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Scissors, Star, Crown, CheckCircle2, Loader2, Zap,
  ArrowRight, Sparkles, CreditCard, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { useLocation } from "wouter";

const PLAN_ICONS: Record<string, React.ReactNode> = {
  basic: <Scissors className="w-6 h-6" />,
  professional: <Star className="w-6 h-6" />,
  premium: <Crown className="w-6 h-6" />,
};

const PLAN_COLORS: Record<string, string> = {
  basic: "border-border hover:border-primary/50",
  professional: "border-primary ring-2 ring-primary/20 shadow-lg",
  premium: "border-amber-400 hover:border-amber-500",
};

export default function PricingPage() {
  const { user, isAuthenticated } = useAuth();
  const [, navigate] = useLocation();
  const [loadingPlanId, setLoadingPlanId] = useState<number | null>(null);

  const { data: plans, isLoading: plansLoading } = trpc.plans.list.useQuery();
  const { data: mySalon } = trpc.salon.getMySalon.useQuery(undefined, { enabled: isAuthenticated });

  const activePlanId = useMemo(() => mySalon?.planId ?? null, [mySalon]);

  const checkoutMutation = trpc.salon.createSubscriptionCheckout.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        toast.success("Redirecionando para o checkout...");
        window.open(data.url, "_blank");
      }
      setLoadingPlanId(null);
    },
    onError: (err) => {
      toast.error(err.message || "Erro ao criar checkout. Tente novamente.");
      setLoadingPlanId(null);
    },
  });

  const billingPortalMutation = trpc.salon.createBillingPortal.useMutation({
    onSuccess: (data) => {
      if (data.url) {
        window.open(data.url, "_blank");
      }
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSelectPlan = (planId: number) => {
    if (!isAuthenticated) {
      navigate("/onboarding");
      return;
    }
    if (!mySalon) {
      // Redirecionar para onboarding se não tem salão
      navigate("/onboarding");
      return;
    }
    setLoadingPlanId(planId);
    checkoutMutation.mutate({ planId, origin: window.location.origin });
  };

  const handleBillingPortal = () => {
    billingPortalMutation.mutate({ origin: window.location.origin });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="border-b border-border/50 bg-white/90 backdrop-blur-md sticky top-0 z-50">
        <div className="container flex items-center justify-between h-16">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <Scissors className="w-5 h-5 text-primary" />
            <span className="font-semibold text-foreground" style={{ fontFamily: "Cormorant Garamond, serif", fontSize: "1.2rem" }}>
              Beleza & Bronze
            </span>
          </button>
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                <span className="text-sm text-muted-foreground hidden sm:block">Olá, {user?.name}</span>
                <Button size="sm" variant="outline" onClick={() => navigate("/admin")}>
                  Painel Admin
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <button className="text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate("/login")}>Entrar</button>
                <Button size="sm" className="bg-primary text-primary-foreground" onClick={() => navigate("/onboarding")}>
                  Criar conta
                </Button>
              </div>
            )}
          </div>
        </div>
      </nav>

      <div className="container py-16 max-w-5xl">
        {/* Hero */}
        <div className="text-center mb-14">
          <Badge variant="secondary" className="mb-4 px-4 py-1 text-sm">
            <Sparkles className="w-3 h-3 mr-1 inline" /> 15 dias grátis em todos os planos
          </Badge>
          <h1 className="text-5xl font-light text-foreground mb-4" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Escolha o plano ideal
          </h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Gerencie seu salão com profissionalismo. Cancele quando quiser, sem multas.
          </p>
        </div>

        {/* Plans */}
        {plansLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-primary" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {(plans ?? []).filter((p) => p.isActive).map((plan) => {
              const isCurrentPlan = activePlanId === plan.id;
              const isLoading = loadingPlanId === plan.id;

              return (
                <Card
                  key={plan.id}
                  className={`relative transition-all duration-200 ${PLAN_COLORS[plan.slug] ?? "border-border"}`}
                >
                  {plan.slug === "professional" && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <Badge className="bg-primary text-primary-foreground px-4 py-1 text-xs font-medium shadow-sm">
                        Mais Popular
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="w-11 h-11 bg-primary/10 rounded-xl flex items-center justify-center text-primary">
                        {PLAN_ICONS[plan.slug] ?? <Zap className="w-6 h-6" />}
                      </div>
                      {isCurrentPlan && (
                        <Badge variant="outline" className="text-green-600 border-green-300 bg-green-50 text-xs">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Plano Atual
                        </Badge>
                      )}
                    </div>
                    <CardTitle className="text-xl font-semibold">{plan.name}</CardTitle>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="text-4xl font-light text-foreground">
                        R$ {Number(plan.priceMonthly).toFixed(0)}
                      </span>
                      <span className="text-muted-foreground text-sm">/mês</span>
                    </div>
                    {plan.description && (
                      <CardDescription className="text-sm mt-1">{plan.description}</CardDescription>
                    )}
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <ul className="space-y-2.5 text-sm">
                      <li className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        Até {plan.maxProfessionals} profissional{plan.maxProfessionals > 1 ? "is" : ""}
                      </li>
                      <li className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        Calendário e agendamentos ilimitados
                      </li>
                      <li className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        App instalável no celular (PWA)
                      </li>
                      {plan.hasAiChatbot && (
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Chatbot de IA para clientes
                        </li>
                      )}
                      {plan.hasOnlinePayment && (
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Pagamento online (Stripe)
                        </li>
                      )}
                      {plan.hasVoiceNotes && (
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Notas de voz com transcrição IA
                        </li>
                      )}
                      {plan.hasAdvancedReports && (
                        <li className="flex items-center gap-2 text-muted-foreground">
                          <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          Relatórios avançados e analytics
                        </li>
                      )}
                      <li className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        Sistema de avaliações de clientes
                      </li>
                      <li className="flex items-center gap-2 text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                        Google Maps integrado
                      </li>
                    </ul>

                    {isCurrentPlan && mySalon?.status === "active" ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleBillingPortal}
                        disabled={billingPortalMutation.isPending}
                      >
                        {billingPortalMutation.isPending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="w-4 h-4 mr-2" />
                        )}
                        Gerenciar Assinatura
                      </Button>
                    ) : (
                      <Button
                        className={`w-full ${plan.slug === "professional" ? "bg-primary text-primary-foreground hover:bg-primary/90" : ""}`}
                        variant={plan.slug === "professional" ? "default" : "outline"}
                        onClick={() => handleSelectPlan(plan.id)}
                        disabled={isLoading || isCurrentPlan}
                      >
                        {isLoading ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Aguarde...</>
                        ) : isCurrentPlan ? (
                          <><CheckCircle2 className="w-4 h-4 mr-2" /> Plano Atual</>
                        ) : (
                          <>Assinar Agora <ArrowRight className="ml-2 w-4 h-4" /></>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Trust signals */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
          {[
            { icon: <ShieldCheck className="w-6 h-6 text-green-500 mx-auto mb-2" />, title: "Pagamento Seguro", desc: "Processado pelo Stripe com criptografia SSL" },
            { icon: <Sparkles className="w-6 h-6 text-primary mx-auto mb-2" />, title: "14 Dias Grátis", desc: "Experimente sem precisar de cartão de crédito" },
            { icon: <CheckCircle2 className="w-6 h-6 text-blue-500 mx-auto mb-2" />, title: "Cancele Quando Quiser", desc: "Sem multas ou fidelidade obrigatória" },
          ].map((item) => (
            <div key={item.title} className="p-6 rounded-xl bg-muted/30 border border-border/50">
              {item.icon}
              <p className="font-medium text-foreground text-sm">{item.title}</p>
              <p className="text-muted-foreground text-xs mt-1">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* FAQ */}
        <div className="mt-16">
          <h2 className="text-2xl font-light text-center mb-8" style={{ fontFamily: "Cormorant Garamond, serif" }}>
            Perguntas Frequentes
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { q: "Preciso de cartão para o trial?", a: "Não. Os 15 dias de teste são completamente gratuitos e não exigem cartão de crédito." },
              { q: "Posso mudar de plano depois?", a: "Sim. Você pode fazer upgrade ou downgrade do seu plano a qualquer momento pelo portal de assinatura." },
              { q: "Como funciona o pagamento?", a: "O pagamento é mensal e automático via Stripe. Você recebe uma nota fiscal por e-mail a cada cobrança." },
              { q: "O que acontece se eu cancelar?", a: "Seu acesso continua até o fim do período pago. Não há multas ou cobranças adicionais." },
            ].map((item) => (
              <div key={item.q} className="p-5 rounded-xl border border-border/50">
                <p className="font-medium text-foreground text-sm mb-1">{item.q}</p>
                <p className="text-muted-foreground text-sm">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
